import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import LoginPanel from './Panels/LoginPanel/LoginPanel';
import AlreadyLoggedInPanel from './Panels/AlreadyLoggedInPanel/AlreadyLoggedInPanel';
import MfaVerifyPanel from './Panels/MfaVerifyPanel/MfaVerifyPanel';
import VerifyAnotherWayConfirmPanel from '../../shared-panels/uam/VerifyAnotherWayConfirmPanel/VerifyAnotherWayConfirmPanel';
import {
  UamHeading,
  UamBodyText,
  UamFlexColumn,
  UamPageIcon,
} from '../../styles-and-themes/styles/uam-registration-styles';
import { composeDestinationQuerystring, getReturnUrlByDestination } from '../../../shared-with-server/destination-utils';
import { destinationInfo, frontEndUrl } from '../../destructured-front-end-config';
import { isIdTokenValid } from '../../utils/jwt-utils';
import { getURLParamByName } from '../../../shared-with-server/product-name-utils';
import UAMLoginRegistrationModal from '../../components/modals/UAMLoginRegistrationModal/UAMLoginRegistrationModal';

const FORWARD_TO_INBOX_ICON_URL = `${frontEndUrl}static/images/forward_to_inbox.svg`;
const CHECK_CIRCLE_ICON_URL = `${frontEndUrl}static/images/check_circle.svg`;

const LoginUAMPage = ({ brand, destPath }) => {
  const redirectToDestPath = useCallback(() => {
    window.location.href = getReturnUrlByDestination(destinationInfo, destPath.dest, destPath.path) || destPath.retURL; // Redirect to home
  }, [destPath]);

  const includeGoogleSignIn = useRef(getURLParamByName(location?.search, 'googleSignIn')?.toLowerCase() === 'true');
  const isPostReset = useRef(getURLParamByName(location?.search, 'postReset') === '1');
  const isAddingAccount = getURLParamByName(location?.search, 'addAccount')?.toLowerCase() === 'true';
  const [validIdToken, setValidIdToken] = useState(isIdTokenValid());
  const [submitData, setSubmitData] = useState(isPostReset.current ? { postResetComplete: true } : null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [registrationStepPending, setRegistrationStepPending] = useState(null); // indicates which step to redirect to following a successful login with registration data remaining
  const [mfaPending, setMfaPending] = useState(null); // { maskedPhone } when MFA OTP challenge is required
  const [verifyAnotherWayStep, setVerifyAnotherWayStep] = useState(null); // null | 'confirm' | 'sent'
  const [verifyAnotherWaySubmitting, setVerifyAnotherWaySubmitting] = useState(false);
  const [verifyAnotherWayError, setVerifyAnotherWayError] = useState(null);
  const [verifyAnotherWayEmail, setVerifyAnotherWayEmail] = useState(null);

  const onSignOut = () => {
    axios.delete('/api/v2/token')
      .then((res) => {
        setSubmitData(null);

        if (res.status === 200) {
          setValidIdToken(false);
        } else {
          setErrorMessage(`Unexpected error at logout (${res.status})`);
        }
      }).catch((err) => {
        setSubmitData(null);
        setErrorMessage(err?.response?.data?.message || 'Unknown error at logout');
      });
  };

  const onSendEmailFallback = async () => {
    if (verifyAnotherWaySubmitting) return;
    setVerifyAnotherWaySubmitting(true);
    setVerifyAnotherWayError(null);
    try {
      const res = await axios.post(
        '/api/v2/fi/token/mfa',
        { sendEmailFallback: true },
        { validateStatus: (s) => s < 500 || s >= 600 },
      );
      if (res.status === 200) {
        setVerifyAnotherWayEmail(res.data?.email || null);
        setVerifyAnotherWayStep('sent');
        return;
      }
      if (res.status === 401) {
        // mfaLoginToken cookie is gone — bounce back to login.
        setMfaPending(null);
        setVerifyAnotherWayStep(null);
        setErrorMessage('Your session has expired. Please sign in again.');
        return;
      }
      setVerifyAnotherWayError('Failed to send verification email. Please try again.');
    } catch (_err) {
      setVerifyAnotherWayError('Failed to send verification email. Please try again.');
    } finally {
      setVerifyAnotherWaySubmitting(false);
    }
  };

  const getPanel = () => {
    if (isPostReset.current && submitData && !errorMessage && !mfaPending && !registrationStepPending) {
      return (
        <UamFlexColumn gap="16px">
          <UamPageIcon src={CHECK_CIRCLE_ICON_URL} alt="" />
          <UamHeading>Signing you in...</UamHeading>
        </UamFlexColumn>
      );
    }

    if (validIdToken && !isAddingAccount) {
      return (
        <AlreadyLoggedInPanel
          onBack={redirectToDestPath}
          onSignOut={onSignOut}
        />
      );
    }

    if (mfaPending && verifyAnotherWayStep === 'sent') {
      return (
        <UamFlexColumn gap="16px">
          <UamPageIcon src={FORWARD_TO_INBOX_ICON_URL} alt="" />
          <UamHeading>Check your email.</UamHeading>
          <UamBodyText id="mfa-vaw-sent-email">
            {"We've sent a verification link to "}
            {verifyAnotherWayEmail
              ? <strong style={{ wordBreak: 'break-all' }}>{verifyAnotherWayEmail}</strong>
              : 'your email address'}
            . Click the link to finish signing in.
          </UamBodyText>
        </UamFlexColumn>
      );
    }

    if (mfaPending && verifyAnotherWayStep === 'confirm') {
      return (
        <VerifyAnotherWayConfirmPanel
          onSendEmail={onSendEmailFallback}
          onCancel={() => {
            setVerifyAnotherWayStep(null);
            setVerifyAnotherWayError(null);
          }}
          isSubmitting={verifyAnotherWaySubmitting}
          errorMessage={verifyAnotherWayError}
        />
      );
    }

    if (mfaPending) {
      return (
        <MfaVerifyPanel
          onSuccess={() => setSubmitData({ mfaComplete: true })}
          onBack={() => setMfaPending(null)}
          onVerifyAnotherWay={() => {
            setVerifyAnotherWayError(null);
            setVerifyAnotherWayStep('confirm');
          }}
        />
      );
    }

    if (registrationStepPending) {
      return (
        <UAMLoginRegistrationModal
          continueClickedFn={() => {
            window.location.href = `/uam/step${registrationStepPending}?${composeDestinationQuerystring(destinationInfo, destPath)}`;
          }}
        />
      );
    }

    return (
      <LoginPanel
        destPath={destPath}
        errorMessage={errorMessage}
        setSubmitData={setSubmitData}
        includeGoogleSignIn={includeGoogleSignIn.current}
        initialEmail={getURLParamByName(location?.search, 'email')}
      />
    );
  };

  useEffect(() => {
    if (!submitData) { return; }

    axios.post(
      '/api/v2/fi/token',
      {
        ...submitData,
        addAccount: isAddingAccount,
      },
      {
        validateStatus: (status) => status < 500 || status >= 600,
      },
    ).then((res) => {
      if (res.data?.subStatus === 'mfaRequired') {
        setMfaPending(true);
        return;
      }

      if (res.data?.subStatus === 'registrationRequired') {
        setRegistrationStepPending(res.data.step);
        return;
      }

      if (res.data?.message) {
        setErrorMessage(res.data.message);
      } else if (res.status === 200) {
        redirectToDestPath();
      } else {
        throw new Error('Unknown error at login');
      }
    }).catch((err) => {
      setErrorMessage(err.message || 'Unknown error at login');
    });
  }, [submitData, isAddingAccount, redirectToDestPath]);

  return (
    <BasePageUAM
      activeTab={1}
      brand={brand}
      destPath={destPath}
      needsDestPath
      showTabs={!mfaPending && !verifyAnotherWayStep}
    >
      {getPanel()}
    </BasePageUAM>
  );
};

LoginUAMPage.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
};

export default LoginUAMPage;
